import { EyeOutlined } from "@ant-design/icons"
import { Button, Flex, Form, Skeleton, Space, Typography } from "antd"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { type ConnectedProps, connect } from "react-redux"
import { useParams } from "react-router"
import { useNavigate, useSearchParams } from "react-router-dom"

import { FixedContainer } from "../../../components/FixedContainer.tsx"
import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { RoutesPaths } from "../../../constants/routes.ts"
import { useDateFormat } from "../../../hooks/use-date-format.ts"
import type { RootState } from "../../../store.ts"
import mergeObjects from "../../../utils/object/merge-objects.ts"
import { getContractSyncStatus } from "../../DocumentSync/api/services.ts"
import { ContractGenerateConfirmModal } from "../../DocumentSync/ContractGenerateConfirmModal.tsx"
import {
  type ContractDiffRow,
  buildContractGenerateDiffRows
} from "../../DocumentSync/contract-generate-diff.ts"
import { getContractValidation } from "../ContractValidation/api/services.ts"
import { contractValidationHasWarnings } from "../ContractValidation/calculate-contract-validation.ts"
import { ContractValidationCard } from "../ContractValidation/ContractValidationCard.tsx"
import { ContractValidationConfirmModal } from "../ContractValidation/ContractValidationConfirmModal.tsx"
import type { ContractValidationResult } from "../ContractValidation/types.ts"
import { showDocumentValidationError } from "../helpers/show-document-validation-error.tsx"
import {
  createContractDocument,
  saveContractDocument,
  sendContractDocumentToApproval,
  updateContractDocument
} from "../redux/thunks/document-contract.thunks.ts"
import { fetchProposalDocumentById } from "../redux/thunks/document-proposal.thunks.ts"
import {
  fetchDocumentInitialData,
  fetchDocumentPreview,
  fetchTemplateById
} from "../redux/thunks/document-templates.thunks.ts"
import {
  type Document,
  type DocumentRequestData
} from "../types/document.types.ts"
import { type TemplateDocumentFormData } from "../types/template-document-form-data.tsx"
import type { DocumentInitialData, DocumentTemplate } from "../types/types.ts"
import { DocumentBackToLeadBtn } from "./components/DocumentBackToLeadBtn.tsx"
import { TemplateDocumentForm } from "./components/TemplateDocumentForm/TemplateDocumentForm.tsx"
import { TemplateDocumentPreview } from "./components/TemplateDocumentPreview.tsx"

const { Title, Text } = Typography

interface PendingGenerateConfirm {
  rows: ContractDiffRow[]
  hasProposal: boolean
  documentLabel?: string
  documentId: string
  leadId: string
  kind: "generate" | "approval"
}

function DocumentContractCreatePage({
  getInitialData,
  getTemplate,
  createDocument,
  updateDocument,
  saveDocument,
  sendDocumentToApproval,
  fetchDocumentPreview,
  fetchProposalById
}: ConnectedProps<typeof connector>) {
  const [form] = Form.useForm<TemplateDocumentFormData>()

  const { normalizeDate } = useDateFormat()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const params = useParams<{
    templateId?: string
    leadId: string
    proposalId?: string
  }>()

  const templateId = searchParams.get("templateId")
  const proposalId = searchParams.get("proposalId")
  const leadId = params?.leadId as string

  const rawInformationSource = searchParams.get("informationSource")
  const informationSource: "proposal" | "lead" | "blank" | undefined =
    rawInformationSource === "proposal" ||
    rawInformationSource === "lead" ||
    rawInformationSource === "blank"
      ? rawInformationSource
      : undefined

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [template, setTemplate] = useState<DocumentTemplate | null>(null)
  const [initialData, setInitialData] = useState<DocumentInitialData | null>(
    null
  )
  const [proposal, setProposal] = useState<Document | null>(null)
  // Caches the just-created document's id (and its own has_proposal flag,
  // needed for the sync-check baseline) once the first save succeeds, so a
  // second click never re-POSTs and creates a duplicate document.
  const [created, setCreated] = useState<{
    id: string
    leadId: string
    hasProposal: boolean
  } | null>(null)
  const [generateConfirmOpen, setGenerateConfirmOpen] = useState(false)
  // One logical "pending confirmation" record — the rows/baseline/label the
  // modal renders plus the documentId/leadId/kind `handleGenerateConfirmProceed`
  // finishes with, all set together by `runWithSyncCheck` and read together
  // here, so a single object avoids four states drifting out of sync with
  // each other (mirrors `created` above, which bundles its own multi-field
  // result the same way).
  const [pendingGenerateConfirm, setPendingGenerateConfirm] =
    useState<PendingGenerateConfirm | null>(null)
  const [validationConfirmOpen, setValidationConfirmOpen] = useState(false)
  const [validationResult, setValidationResult] =
    useState<ContractValidationResult | null>(null)
  // The `{id, leadId}` a confirmed "Generate Anyway" resumes with — only
  // ever set right before opening the validation modal, so a stale value
  // can never resume a check that's still in progress.
  const [pendingValidationConfirm, setPendingValidationConfirm] = useState<{
    id: string
    leadId: string
    hasProposal: boolean
  } | null>(null)

  const IDS = useMemo(() => {
    return {
      lead_id: initialData?.lead_id || leadId || proposal?.lead_id || "",
      customer_id: initialData?.customer_id || proposal?.customer_id || "",
      template_id: templateId || initialData?.template_id,
      information_source: informationSource,
      // The specific Proposal the user chose — either auto-pinned when the
      // Lead has exactly one, or picked explicitly via the multi-Proposal
      // `ContractProposalPickerStep` — without this the backend silently
      // re-resolves "latest relevant proposal" on its own, ignoring
      // whichever one was actually shown/selected on screen.
      proposal_id: proposalId || proposal?.id || undefined
    }
  }, [
    informationSource,
    initialData?.customer_id,
    initialData?.lead_id,
    initialData?.template_id,
    leadId,
    proposal?.customer_id,
    proposal?.id,
    proposal?.lead_id,
    proposalId,
    templateId
  ])

  const handleFetchDocumentPreview = useCallback(
    async (data: DocumentRequestData) => {
      return fetchDocumentPreview({
        ...data,
        _meta: { doc_type: "contract" }
      }).unwrap()
    },
    [fetchDocumentPreview]
  )

  const handleCreateDocument = useCallback(async () => {
    try {
      const values = await form.validateFields()

      return createDocument({
        ...IDS,
        ...values,
        completion_date: normalizeDate(values?.completion_date),
        start_date: normalizeDate(values?.start_date)
      }).unwrap()
    } catch (e) {
      showDocumentValidationError(form, e)
      console.log(e)
    }
  }, [createDocument, form, IDS])

  // Same shape as `DocumentContractEditPage`'s own `handleUpdateDocument` —
  // PATCHes the form's current values to an already-created document. Errors
  // (validation included) are handled here, not rethrown, matching
  // `handleCreateDocument`'s own swallow-and-return-undefined convention so
  // `ensureCreated` can treat "create failed validation" and "re-save failed
  // validation" the same way.
  const handleUpdateDocument = useCallback(
    async (id: string) => {
      try {
        const values = await form.validateFields()
        return await updateDocument({
          id,
          data: {
            ...values,
            completion_date: normalizeDate(values?.completion_date),
            start_date: normalizeDate(values?.start_date)
          }
        })?.unwrap()
      } catch (e) {
        showDocumentValidationError(form, e)
        console.log(e)
        return undefined
      }
    },
    [form, normalizeDate, updateDocument]
  )

  // Creates the document once, then PATCHes the current form values on every
  // subsequent call rather than short-circuiting on the cached id alone.
  // Without the PATCH, a field edited after the first Save (e.g. the user
  // closes the sync-confirm modal without confirming, edits a field, and
  // clicks Save & Generate again) was silently discarded: `saveDocument`/
  // `finishGenerate` below carries only a document id, no field data, so a
  // "no new diff" second run skipped straight to generating a PDF from the
  // stale, first-created values. Mirrors `DocumentContractEditPage.
  // handleSaveAndUpdate`, which always PATCHes before its own sync-check —
  // there, every save already had an existing document to update; here, only
  // the second and later saves do.
  const ensureCreated = useCallback(async () => {
    if (created) {
      const updated = await handleUpdateDocument(created.id)
      if (!updated) return null
      return created
    }

    const createdDocument = await handleCreateDocument()
    const id = createdDocument?.id
    const docLeadId = createdDocument?.lead_id
    if (!id || !docLeadId) return null

    const result = {
      id,
      leadId: docLeadId,
      hasProposal: !!createdDocument?.has_proposal
    }
    setCreated(result)
    return result
  }, [created, handleCreateDocument, handleUpdateDocument])

  // save draft without pdf
  const handleSaveDraft = useCallback(async () => {
    try {
      setSaving(true)
      const result = await ensureCreated()
      if (!result) return

      navigate(RoutesPaths.lead.view(result.leadId))
    } catch (e) {
      console.log(e)
    } finally {
      setSaving(false)
    }
  }, [ensureCreated, navigate])

  // The actual PDF-render/save-file step — shared by the no-diff path
  // (called immediately) and `ContractGenerateConfirmModal`'s resolution,
  // whichever boxes were checked (including neither), after any checked
  // syncs complete.
  const finishGenerate = useCallback(
    async (documentId: string, docLeadId: string) => {
      try {
        setSaving(true)
        await saveDocument(documentId)
        navigate(RoutesPaths.lead.view(docLeadId, { tab: "documents" }))
      } catch (e) {
        console.log(e)
      } finally {
        setSaving(false)
      }
    },
    [navigate, saveDocument]
  )

  const finishSendToApproval = useCallback(
    async (documentId: string, docLeadId: string) => {
      try {
        setSaving(true)
        await sendDocumentToApproval(documentId).unwrap()
        navigate(RoutesPaths.lead.view(docLeadId))
      } catch (e) {
        console.log(e)
      } finally {
        setSaving(false)
      }
    },
    [navigate, sendDocumentToApproval]
  )

  // Same gap, same fix as the Proposal create page: "Save & Generate"/"Send
  // to Approval" on a brand-new Contract used to create-and-navigate with
  // no comparison against its baseline (Proposal or Lead) at all —
  // `ContractGenerateConfirmModal` only ever appeared later, on a
  // subsequent edit-and-regenerate, because only `DocumentContractEditPage`
  // ran this check. `hasLinkedProposal` comes from the just-created
  // document's own `has_proposal` (mirroring the edit page reading
  // `document?.has_proposal`) rather than being inferred from this page's
  // own `proposal`/`proposalId` state, since the backend is the source of
  // truth for what actually got linked. Extracted from `runWithSyncCheck`
  // so the Contract Validation gate below can resume at this exact point
  // after "Generate Anyway", without duplicating it.
  const proceedWithSyncCheck = useCallback(
    async (
      result: { id: string; leadId: string; hasProposal: boolean },
      kind: "generate" | "approval"
    ) => {
      const statusRes = await getContractSyncStatus(result.id)
      const fields = statusRes.ok ? (statusRes.data?.fields ?? []) : []
      const rows = buildContractGenerateDiffRows(fields, result.hasProposal)

      if (!rows.length) {
        if (kind === "generate") {
          await finishGenerate(result.id, result.leadId)
        } else {
          await finishSendToApproval(result.id, result.leadId)
        }
        return
      }

      setPendingGenerateConfirm({
        rows,
        hasProposal: result.hasProposal,
        documentLabel: statusRes.ok ? statusRes.data?.document_label : undefined,
        documentId: result.id,
        leadId: result.leadId,
        kind
      })
      setGenerateConfirmOpen(true)
    },
    [finishGenerate, finishSendToApproval]
  )

  const runWithSyncCheck = useCallback(
    async (kind: "generate" | "approval") => {
      try {
        setSaving(true)
        const result = await ensureCreated()
        if (!result) return

        // The validation gate only applies to "Generate Contract" itself,
        // per spec — "Send to Approval" is unaffected. Same
        // never-block-on-failure swallow as the Edit page: an
        // unreachable/erroring check must not stop Generate.
        if (kind === "generate") {
          let validation: ContractValidationResult | null = null
          try {
            const validationRes = await getContractValidation(result.id)
            validation = validationRes.ok ? (validationRes.data ?? null) : null
          } catch {
            validation = null
          }

          if (validation && contractValidationHasWarnings(validation)) {
            setValidationResult(validation)
            setPendingValidationConfirm(result)
            setValidationConfirmOpen(true)
            return
          }
        }

        await proceedWithSyncCheck(result, kind)
      } catch (e) {
        console.log(e)
      } finally {
        setSaving(false)
      }
    },
    [ensureCreated, proceedWithSyncCheck]
  )

  const handleValidationConfirmProceed = useCallback(async () => {
    if (!pendingValidationConfirm) return
    try {
      setSaving(true)
      await proceedWithSyncCheck(pendingValidationConfirm, "generate")
    } catch (e) {
      console.log(e)
    } finally {
      setSaving(false)
    }
  }, [pendingValidationConfirm, proceedWithSyncCheck])

  // save pdf
  const handleSaveDocument = useCallback(
    () => runWithSyncCheck("generate"),
    [runWithSyncCheck]
  )

  const handleSendToApproval = useCallback(
    () => runWithSyncCheck("approval"),
    [runWithSyncCheck]
  )

  const handleGenerateConfirmProceed = useCallback(async () => {
    if (!pendingGenerateConfirm) return

    if (pendingGenerateConfirm.kind === "generate") {
      await finishGenerate(
        pendingGenerateConfirm.documentId,
        pendingGenerateConfirm.leadId
      )
    } else {
      await finishSendToApproval(
        pendingGenerateConfirm.documentId,
        pendingGenerateConfirm.leadId
      )
    }
  }, [pendingGenerateConfirm, finishGenerate, finishSendToApproval])

  const handleGetAllData = useCallback(async () => {
    if (!leadId) {
      return console.error("Lead id are required to fetch initial data")
    }

    try {
      if (templateId) {
        const initDataRes = await getInitialData({
          templateId,
          leadId
        }).unwrap()
        setInitialData(initDataRes)
      }

      if (proposalId) {
        const proposalRes = await fetchProposalById(proposalId).unwrap()
        setProposal(proposalRes)
      }

      if (templateId) {
        const templateRes = await getTemplate(templateId).unwrap()
        setTemplate(templateRes)
      }
    } finally {
      setLoading(false)
    }
  }, [
    fetchProposalById,
    getInitialData,
    getTemplate,
    leadId,
    proposalId,
    templateId
  ])

  const handleSubmit = useCallback(
    async (data: TemplateDocumentFormData) => {
      try {
        setSaving(true)
        await handleSaveDocument()
      } catch (e) {
        console.log()
      } finally {
        setSaving(false)
      }
    },
    [handleSaveDocument]
  )

  const mergedInitialValues = useMemo(() => {
    if (proposal) return proposal
    if (!initialData) return undefined
    if (!template) return initialData

    const mergedData = mergeObjects(initialData, template, {
      base: "first",
      mergeExtra: true
    })

    return { ...(mergedData || {}), phases: initialData?.phases || [] }
  }, [initialData, proposal, template])

  useEffect(() => {
    void handleGetAllData()
    generateDocumentTitle("Create Document")
  }, [handleGetAllData])

  if (!leadId) return <div>Lead id is required</div>
  if (loading) return <Skeleton.Node active className={"size-full"} />

  return (
    <FixedContainer>
      <ContractValidationConfirmModal
        open={validationConfirmOpen}
        onClose={() => setValidationConfirmOpen(false)}
        result={validationResult}
        onProceed={handleValidationConfirmProceed}
      />
      <ContractGenerateConfirmModal
        open={generateConfirmOpen}
        onClose={() => setGenerateConfirmOpen(false)}
        rows={pendingGenerateConfirm?.rows ?? []}
        contractId={pendingGenerateConfirm?.documentId ?? ""}
        hasLinkedProposal={pendingGenerateConfirm?.hasProposal ?? false}
        documentLabel={pendingGenerateConfirm?.documentLabel}
        onProceed={handleGenerateConfirmProceed}
      />
      <Flex vertical gap={"small"} className={"overflow-hidden"}>
        <Flex justify={"space-between"} align={"center"} wrap gap={"small"}>
          <Flex vertical>
            <Title level={4}>Create Document</Title>
            {template && (
              <Text type="secondary">
                Template: {template?.name || "No name provided"}
              </Text>
            )}
            {proposal && (
              <Text type="secondary">
                Created based on: {proposal?.proposal_id}
              </Text>
            )}
          </Flex>
          <Space wrap>
            {leadId && (
              <DocumentBackToLeadBtn leadId={leadId} saving={saving} />
            )}
            <TemplateDocumentPreview
              saving={saving}
              form={form}
              templateId={IDS?.template_id}
              leadId={IDS?.lead_id}
              customerId={IDS?.customer_id}
              fetchDocumentPreview={handleFetchDocumentPreview}
              submitLabel={"Save as Draft"}
              onSubmitClick={handleSaveDraft}
              onGenerateClick={handleSaveDocument}
              onClickSendToApproval={handleSendToApproval}
            />

            <Button
              loading={saving}
              onClick={form.submit}
              variant={"solid"}
              color={"primary"}
              icon={<EyeOutlined />}
            >
              Save & Generate
            </Button>
          </Space>
        </Flex>

        <ContractValidationCard form={form} className={"mb-3"} />

        <TemplateDocumentForm
          form={form}
          saving={saving}
          initialValues={mergedInitialValues}
          projectTypes={
            proposal?.project_type_details ||
            initialData?.project_type_details ||
            []
          }
          leadId={IDS?.lead_id}
          onSubmit={handleSubmit}
        />
      </Flex>
    </FixedContainer>
  )
}

const mapStateToProps = (state: RootState) => ({})

const mapDispatchToProps = {
  // getBlocks: fetchAvailableBlocks,
  getInitialData: fetchDocumentInitialData,
  getTemplate: fetchTemplateById,
  createDocument: createContractDocument,
  updateDocument: updateContractDocument,
  saveDocument: saveContractDocument,
  sendDocumentToApproval: sendContractDocumentToApproval,
  fetchDocumentPreview,
  fetchProposalById: fetchProposalDocumentById
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(DocumentContractCreatePage)
